import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';

export default function CustomSelect({
  id,
  value = '',
  options = [],
  onChange,
  required = false,
  invalid = false,
  disabled = false,
  className = '',
  placeholder,
  includeEmpty = false,
  'aria-label': ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState({});
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const listId = useId();

  const placeholderOpt = options.find((opt) => opt.value === '' || opt.value == null);
  const listOptions = includeEmpty
    ? options
    : options.filter((opt) => opt.value !== '' && opt.value != null);
  const selected = listOptions.find((opt) => String(opt.value) === String(value));
  const isPlaceholder = !includeEmpty && (value === '' || value == null);
  const placeholderText = placeholder || placeholderOpt?.label || 'Оберіть…';
  const label = isPlaceholder ? placeholderText : (selected?.label || placeholderText || '—');

  const updatePanelPos = () => {
    if (!rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    const gap = 8;
    const pad = 12;
    const cap = 220;
    const spaceBelow = window.innerHeight - rect.bottom - pad;
    const spaceAbove = rect.top - pad;
    const openUp = spaceBelow < 128 && spaceAbove > spaceBelow;
    const maxH = Math.min(cap, Math.max(48, openUp ? spaceAbove : spaceBelow));
    const contentH = panelRef.current?.scrollHeight || listOptions.length * 40 + 12;
    const panelH = Math.min(maxH, contentH);

    setPanelStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      right: 'auto',
      bottom: 'auto',
      height: 'auto',
      zIndex: 10050,
      maxHeight: maxH,
      top: openUp
        ? Math.max(pad, rect.top - gap - panelH)
        : rect.bottom + gap,
    });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePanelPos();
    const raf = requestAnimationFrame(() => updatePanelPos());
    const onScroll = () => updatePanelPos();
    window.addEventListener('resize', onScroll);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, listOptions.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const inTrigger = rootRef.current?.contains(e.target);
      const inPanel = panelRef.current?.contains(e.target);
      if (!inTrigger && !inPanel) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const panel =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={panelRef}
            className="custom-select-panel custom-select-panel--portal"
            role="listbox"
            id={listId}
            aria-labelledby={id}
            style={panelStyle}
          >
            <ul className="custom-select-list">
              {listOptions.map((opt) => {
                const active = String(opt.value) === String(value);
                return (
                  <li key={String(opt.value)}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      className={`custom-select-option${active ? ' is-selected' : ''}`}
                      onClick={() => {
                        onChange?.(opt.value);
                        setOpen(false);
                      }}
                    >
                      {opt.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={rootRef}
      className={`custom-select${open ? ' is-open' : ''}${invalid ? ' is-error' : ''}${disabled ? ' is-disabled' : ''}${className ? ` ${className}` : ''}`}
    >
      <button
        type="button"
        id={id}
        className={`custom-select-trigger${isPlaceholder ? ' is-placeholder' : ''}${invalid ? ' input-error' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-invalid={invalid || undefined}
        aria-required={required || undefined}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
      >
        <span className="custom-select-value">{label}</span>
        <Icon icon="lucide:chevron-down" className="custom-select-chevron" aria-hidden />
      </button>
      {panel}
    </div>
  );
}
